import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const FlyTo = ({ position }) => {
  const map = useMap();
  const prev = useRef(null);
  useEffect(() => {
    if (position && position !== prev.current) {
      map.flyTo(position, 15, { duration: 1.2 });
      prev.current = position;
    }
  }, [position, map]);
  return null;
};

const ClickHandler = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick(e.latlng) });
  return null;
};

const MapPicker = ({ lat, lng, onChange }) => {
  const defaultCenter = [31.7917, -7.0926]; // Morocco center
  const position = lat && lng ? [parseFloat(lat), parseFloat(lng)] : null;

  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [flyTarget, setFlyTarget] = useState(position);

  const handlePick = ({ lat, lng }) => {
    onChange({ lat, lng });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(search)}&format=json&limit=1`,
        { headers: { "Accept-Language": "fr" } }
      );
      const data = await res.json();
      if (data.length > 0) {
        const { lat, lon } = data[0];
        const newPos = { lat: parseFloat(lat), lng: parseFloat(lon) };
        onChange(newPos);
        setFlyTarget([newPos.lat, newPos.lng]);
      }
    } catch (_) {}
    setSearching(false);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          className="flex-1 bg-gray-100 border border-gray-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:bg-white focus:border-gray-400 transition"
          placeholder="Rechercher une adresse..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="submit"
          disabled={searching}
          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition disabled:opacity-60"
        >
          {searching ? "..." : "Chercher"}
        </button>
      </form>

      {/* Map */}
      <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm" style={{ height: 340 }}>
        <MapContainer
          center={position || defaultCenter}
          zoom={position ? 15 : 6}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          <ClickHandler onPick={handlePick} />
          {flyTarget && <FlyTo position={flyTarget} />}
          {position && <Marker position={position} />}
        </MapContainer>
      </div>

      {/* Coordinates display */}
      <div className="flex gap-4">
        <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <span className="text-xs text-gray-400 font-semibold uppercase">Latitude</span>
          <p className="text-sm text-gray-700 font-mono mt-0.5">{lat ? parseFloat(lat).toFixed(6) : "—"}</p>
        </div>
        <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <span className="text-xs text-gray-400 font-semibold uppercase">Longitude</span>
          <p className="text-sm text-gray-700 font-mono mt-0.5">{lng ? parseFloat(lng).toFixed(6) : "—"}</p>
        </div>
        {(lat || lng) && (
          <button
            type="button"
            onClick={() => onChange({ lat: null, lng: null })}
            className="px-3 py-2 text-xs text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition font-semibold"
          >
            Effacer
          </button>
        )}
      </div>

      <p className="text-xs text-gray-400">
        Cliquez sur la carte pour placer le marqueur, ou utilisez la recherche.
      </p>
    </div>
  );
};

export default MapPicker;
